import { useState, useEffect, useCallback } from "react";
import {
  Building2,
  Globe,
  FileText,
  Hash,
  Users,
  Calendar,
  CheckCircle2,
  AlertCircle,
  Gauge,
  Crown,
  Loader2,
  ArrowUpCircle,
  Mail,
} from "lucide-react";
import { useOrgContext } from "../../../context/OrgContext";
import { useAuth } from "../../../context/AuthContext";
import { useLocale } from "../../../context/LanguageContext";
import { ImageUploader } from "../../../components/ui/ImageUploader";
import { useImageUpload } from "../../../hooks/useImageUpload";
import {
  useOrganizationSettings,
  useOrganizationRosterStats,
  useUpdateOrganizationSettings,
  useUpdateOrganizationTier,
  useEnrollPendingRosterUsers,
} from "../hooks/useOrganizationSettings";
import {
  ORG_TYPES,
  ORG_TYPE_LABEL_KEYS,
  ORG_TYPE_EMOJI,
  ORG_TIERS,
  ORG_TIER_LABELS,
  ORG_TIER_DESCRIPTION_KEYS,
} from "../../organizations/constants";
import { IntegrationsSection } from "../../integrations";
import type { OrganizationType, OrganizationTier, SeatUsageStatus, Organization } from "../../organizations/types";

type ToastState = {
  type: "success" | "error";
  message: string;
} | null;

export function OrganizationSettingsPage() {
  const { user } = useAuth();
  const { currentOrg, currentOrgId } = useOrgContext();
  const { t } = useLocale();

  // ── Data fetching ──────────────────────────────────────────────────────────
  const { data, loading: queryLoading } = useOrganizationSettings(
    currentOrgId ?? "",
  );
  const { data: rosterData } = useOrganizationRosterStats(currentOrgId ?? "");
  const [updateOrganization, { loading: updateLoading }] =
    useUpdateOrganizationSettings();

  // ── Tier & Seat mutations (ADMIN only) ─────────────────────────────────
  const [updateTier, { loading: tierUpdateLoading }] = useUpdateOrganizationTier();
  const [enrollPending, { loading: enrollLoading }] = useEnrollPendingRosterUsers();

  const organization = data?.organization ?? null;
  const memberCount = organization?.memberCount ?? 0;
  const rosterCount = rosterData?.organizationRosterStats?.totalEntries ?? 0;

  // ── Image upload ───────────────────────────────────────────────────────────
  const {
    upload: uploadImage,
    uploading: imageUploading,
    error: uploadError,
    reset: resetUpload,
  } = useImageUpload({ purpose: "ORGANIZATION" });

  // ── Form state ─────────────────────────────────────────────────────────────
  const [name, setName] = useState("");
  const [type, setType] = useState<OrganizationType>("COMMERCIAL");
  const [description, setDescription] = useState("");
  const [website, setWebsite] = useState("");
  const [picture, setPicture] = useState<string | undefined>();
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [toast, setToast] = useState<ToastState>(null);

  // Tier editing state (ADMIN only)
  const [editTier, setEditTier] = useState<OrganizationTier | null>(null);
  const [editMaxUsers, setEditMaxUsers] = useState<string>("");
  const [enrollResult, setEnrollResult] = useState<{ enrolledCount: number; skippedCount: number; totalPending: number } | null>(null);

  // Track whether the form has been modified from the fetched data
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize / re-initialize form when org data loads
  useEffect(() => {
    if (organization) {
      setName(organization.name);
      setType(organization.type);
      setDescription(organization.description ?? "");
      setWebsite(organization.website ?? "");
      setPicture(organization.picture);
      setPendingFile(null);
      resetUpload();
      setIsInitialized(true);
    }
  }, [organization, resetUpload]);

  // ── Dirty check ────────────────────────────────────────────────────────────
  const isDirty =
    isInitialized &&
    organization != null &&
    (name.trim() !== organization.name ||
      type !== organization.type ||
      (description.trim() || undefined) !== (organization.description || undefined) ||
      (website.trim() || undefined) !== (organization.website || undefined) ||
      pendingFile !== null ||
      picture !== organization.picture);

  // ── Validation ─────────────────────────────────────────────────────────────
  const nameLength = name.trim().length;
  const isNameValid = nameLength >= 2 && nameLength <= 100;
  const isWebsiteValid =
    !website.trim() || website.trim().length <= 200;
  const isDescriptionValid = description.length <= 2000;
  const canSubmit =
    isNameValid &&
    isWebsiteValid &&
    isDescriptionValid &&
    isDirty &&
    !updateLoading &&
    !imageUploading;

  // ── Toast auto-dismiss ────────────────────────────────────────────────────
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  // ── Submit handler ────────────────────────────────────────────────────────
  const handleSubmit = useCallback(async () => {
    if (!canSubmit || !currentOrgId) return;

    try {
      let pictureUrl = picture;

      // Upload pending image first
      if (pendingFile && currentOrgId) {
        pictureUrl = await uploadImage(pendingFile, currentOrgId);
      }

      await updateOrganization({
        variables: {
          id: currentOrgId,
          input: {
            name: name.trim(),
            type,
            description: description.trim() || undefined,
            website: website.trim() || undefined,
            ...(pictureUrl !== organization?.picture
              ? { picture: pictureUrl ?? null }
              : {}),
          },
        },
      });

      setPendingFile(null);
      setToast({ type: "success", message: t("settings.saved") });
    } catch (err) {
      const message =
        err instanceof Error ? err.message : t("settings.saveFailed");
      setToast({ type: "error", message });
    }
  }, [
    canSubmit,
    currentOrgId,
    pendingFile,
    picture,
    name,
    type,
    description,
    website,
    organization?.picture,
    uploadImage,
    updateOrganization,
  ]);

  // ── File selection handler ────────────────────────────────────────────────
  const handleFileSelect = useCallback((file: File) => {
    setPendingFile(file);
  }, []);

  const handleImageRemove = useCallback(() => {
    setPendingFile(null);
    setPicture(undefined);
    resetUpload();
  }, [resetUpload]);

  // ── Tier update handler (ADMIN only) ──────────────────────────────────────
  const handleTierUpdate = useCallback(async () => {
    if (!currentOrgId || !editTier) return;
    try {
      const customMax = editMaxUsers.trim() === "" ? null : parseInt(editMaxUsers, 10);
      if (customMax !== null && (isNaN(customMax) || customMax < 1)) {
        setToast({ type: "error", message: t("settings.plan.customSeatLimitError") });
        return;
      }
      const result = await updateTier({
        variables: {
          organizationId: currentOrgId,
          input: {
            tier: editTier,
            ...(customMax !== null ? { maxUsers: customMax } : { maxUsers: null }),
          },
        },
      });
      const tierResult = result.data?.updateOrganizationTier;
      setEditTier(null);
      setEditMaxUsers("");
      setToast({ type: "success", message: t("settings.plan.planUpdated", { tier: ORG_TIER_LABELS[editTier] }) });
      if (tierResult && tierResult.pendingEnrollmentCount > 0) {
        setEnrollResult({
          enrolledCount: 0,
          skippedCount: 0,
          totalPending: tierResult.pendingEnrollmentCount,
        });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : t("settings.plan.planUpdateFailed");
      setToast({ type: "error", message });
    }
  }, [currentOrgId, editTier, editMaxUsers, updateTier]);

  // ── Enroll pending users handler (ADMIN only) ────────────────────────────
  const handleEnrollPending = useCallback(async () => {
    if (!currentOrgId) return;
    try {
      const result = await enrollPending({
        variables: { organizationId: currentOrgId },
      });
      const data = result.data?.enrollPendingRosterUsers;
      if (data) {
        setEnrollResult(null);
        setToast({
          type: "success",
          message: t("settings.plan.enrolled", {
            enrolled: data.enrolledCount,
            skipped:
              data.skippedCount > 0
                ? t("settings.plan.skippedSuffix", { count: data.skippedCount })
                : "",
          }),
        });
      }
    } catch (err) {
      console.error("[OrganizationSettingsPage] Enroll pending failed:", err);
      setToast({ type: "error", message: t("settings.plan.enrollFailed") });
    }
  }, [currentOrgId, enrollPending, t]);

  // ── Empty / Loading states ────────────────────────────────────────────────
  if (!currentOrgId || !currentOrg) {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <div className="text-center">
          <Building2 className="mx-auto h-12 w-12 text-muted-foreground/40" />
          <h3 className="mt-4 text-lg font-medium text-foreground">
            {t("settings.noOrgSelected")}
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {user?.role === "ADMIN"
              ? t("settings.selectOrgAdmin")
              : t("settings.noOrgAssigned")}
          </p>
        </div>
      </div>
    );
  }

  if (queryLoading && !organization) {
    return (
      <div className="mx-auto max-w-2xl p-6">
        <div className="space-y-6">
          {/* Skeleton header */}
          <div className="h-8 w-48 animate-pulse rounded-md bg-muted" />
          <div className="h-4 w-72 animate-pulse rounded-md bg-muted" />
          {/* Skeleton card */}
          <div className="rounded-xl border border-border bg-card p-6">
            <div className="space-y-5">
              <div className="flex items-center gap-4">
                <div className="h-20 w-20 animate-pulse rounded-lg bg-muted" />
                <div className="h-4 w-16 animate-pulse rounded-md bg-muted" />
              </div>
              <div className="h-10 w-full animate-pulse rounded-md bg-muted" />
              <div className="flex gap-2">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="h-10 flex-1 animate-pulse rounded-md bg-muted"
                  />
                ))}
              </div>
              <div className="h-24 w-full animate-pulse rounded-md bg-muted" />
              <div className="h-10 w-full animate-pulse rounded-md bg-muted" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!organization) return null;

  return (
    <div className="mx-auto max-w-2xl p-6">
      {/* Page header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground">
          {t("settings.pageTitle")}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("settings.pageDescription")}
        </p>
      </div>

      {/* Toast notification */}
      {toast && (
        <div
          className={`mb-6 flex items-center gap-2 rounded-lg border px-4 py-3 text-sm ${
            toast.type === "success"
              ? "border-success/30 bg-success/10 text-success"
              : "border-destructive/30 bg-destructive/10 text-destructive"
          }`}
        >
          {toast.type === "success" ? (
            <CheckCircle2 className="h-4 w-4 shrink-0" />
          ) : (
            <AlertCircle className="h-4 w-4 shrink-0" />
          )}
          {toast.message}
        </div>
      )}

      {/* Settings card */}
      <div className="rounded-xl border border-border bg-card shadow-sm">
        <div className="space-y-6 p-6">
          {/* Organization Picture */}
          <ImageUploader
            currentImageUrl={picture}
            onFileSelect={handleFileSelect}
            onRemove={handleImageRemove}
            error={uploadError}
            uploading={imageUploading}
            label={t("settings.orgPicture")}
            fallbackHint={t("settings.orgPictureHint")}
          />

          {/* Name */}
          <div>
            <label
              htmlFor="org-settings-name"
              className="block text-sm font-medium text-foreground"
            >
              {t("settings.orgName")} <span className="text-destructive">*</span>
            </label>
            <input
              id="org-settings-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={100}
              placeholder={t("settings.orgNamePlaceholder")}
              className="mt-1.5 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
            <div className="mt-1 flex items-center justify-between">
              {name.length > 0 && !isNameValid && (
                <p className="text-xs text-destructive">
                  {t("settings.orgNameValidation")}
                </p>
              )}
              <p className="ml-auto text-xs text-muted-foreground">
                {nameLength}/100
              </p>
            </div>
          </div>

          {/* Type */}
          <div>
            <label className="block text-sm font-medium text-foreground">
              {t("settings.orgType")}
            </label>
            <div className="mt-1.5 flex gap-2">
              {ORG_TYPES.map((orgType) => (
                <button
                  key={orgType}
                  type="button"
                  onClick={() => setType(orgType)}
                  className={`flex-1 rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
                    type === orgType
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border text-muted-foreground hover:bg-muted hover:text-foreground"
                  }`}
                >
                  <span className="mr-1.5">{ORG_TYPE_EMOJI[orgType]}</span>
                  {t(ORG_TYPE_LABEL_KEYS[orgType])}
                </button>
              ))}
            </div>
          </div>

          {/* Description */}
          <div>
            <label
              htmlFor="org-settings-description"
              className="block text-sm font-medium text-foreground"
            >
              {t("settings.orgDescription")}
            </label>
            <textarea
              id="org-settings-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t("settings.orgDescriptionPlaceholder")}
              rows={4}
              maxLength={2000}
              className="mt-1.5 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
            <div className="mt-1 flex items-center justify-between">
              {!isDescriptionValid && (
                <p className="text-xs text-destructive">
                  {t("settings.orgDescriptionValidation")}
                </p>
              )}
              <p className="ml-auto text-xs text-muted-foreground">
                {description.length}/2000
              </p>
            </div>
          </div>

          {/* Website */}
          <div>
            <label
              htmlFor="org-settings-website"
              className="block text-sm font-medium text-foreground"
            >
              {t("settings.website")}
            </label>
            <div className="relative mt-1.5">
              <Globe className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                id="org-settings-website"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                placeholder="https://example.com"
                maxLength={200}
                className="w-full rounded-md border border-border bg-background py-2 pl-9 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
            {!isWebsiteValid && (
              <p className="mt-1 text-xs text-destructive">
                {t("settings.websiteValidation")}
              </p>
            )}
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-border" />

        {/* Read-only metadata section */}
        <div className="p-6">
          <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            {t("settings.orgInfo")}
          </h3>
          <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
            <div className="flex items-center gap-2">
              <Hash className="h-3.5 w-3.5 text-muted-foreground" />
              <dt className="text-muted-foreground">{t("settings.infoId")}</dt>
              <dd className="ml-auto font-mono text-xs text-foreground">
                {organization._id}
              </dd>
            </div>
            <div className="flex items-center gap-2">
              <Users className="h-3.5 w-3.5 text-muted-foreground" />
              <dt className="text-muted-foreground">{t("settings.infoMembers")}</dt>
              <dd className="ml-auto text-foreground">{memberCount}</dd>
            </div>
            <div className="flex items-center gap-2">
              <FileText className="h-3.5 w-3.5 text-muted-foreground" />
              <dt className="text-muted-foreground">{t("settings.infoRosterEntries")}</dt>
              <dd className="ml-auto text-foreground">{rosterCount}</dd>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-3.5 w-3.5 text-muted-foreground" />
              <dt className="text-muted-foreground">{t("settings.infoStatus")}</dt>
              <dd
                className={`ml-auto font-medium ${
                  organization.isActive ? "text-success" : "text-destructive"
                }`}
              >
                {organization.isActive ? t("settings.statusActive") : t("settings.statusSuspended")}
              </dd>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
              <dt className="text-muted-foreground">{t("settings.infoCreated")}</dt>
              <dd className="ml-auto text-foreground">
                {new Date(organization.createdAt).toLocaleDateString()}
              </dd>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
              <dt className="text-muted-foreground">{t("settings.infoUpdated")}</dt>
              <dd className="ml-auto text-foreground">
                {new Date(organization.updatedAt).toLocaleDateString()}
              </dd>
            </div>
          </dl>
        </div>

        {/* Divider */}
        <div className="border-t border-border" />

        {/* Footer actions */}
        <div className="flex items-center justify-end gap-3 px-6 py-4">
          {isDirty && (
            <p className="mr-auto text-xs text-muted-foreground">
              {t("settings.unsavedChanges")}
            </p>
          )}
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="h-9 rounded-md bg-primary px-5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {updateLoading || imageUploading ? (
              <span className="flex items-center gap-2">
                <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent" />
                {t("settings.saving")}
              </span>
            ) : (
              t("settings.saveChanges")
            )}
          </button>
        </div>
      </div>

      {/* Integrations section */}
      <div className="mt-8">
        <IntegrationsSection />
      </div>

      {/* Plan & Capacity section */}
      <PlanCapacitySection
        organization={organization}
        isAdmin={user?.role === "ADMIN"}
        editTier={editTier}
        setEditTier={setEditTier}
        editMaxUsers={editMaxUsers}
        setEditMaxUsers={setEditMaxUsers}
        onSaveTier={handleTierUpdate}
        tierUpdateLoading={tierUpdateLoading}
        enrollResult={enrollResult}
        onEnrollPending={handleEnrollPending}
        enrollLoading={enrollLoading}
      />
    </div>
  );
}

// ─── Plan & Capacity Section ─────────────────────────────────────────────────

const SEAT_STATUS_COLORS: Record<SeatUsageStatus, string> = {
  OK: "text-success",
  WARNING: "text-amber-500",
  CRITICAL: "text-destructive",
  FULL: "text-destructive",
  UNLIMITED: "text-primary",
};

const SEAT_BAR_COLORS: Record<SeatUsageStatus, string> = {
  OK: "bg-success",
  WARNING: "bg-amber-500",
  CRITICAL: "bg-destructive",
  FULL: "bg-destructive",
  UNLIMITED: "bg-primary",
};

const TIER_BADGE_STYLES: Record<OrganizationTier, string> = {
  SEED: "bg-muted text-foreground",
  SCALE: "bg-primary/10 text-primary",
  ENTERPRISE: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
};

interface PlanCapacitySectionProps {
  organization: Organization;
  isAdmin: boolean;
  editTier: OrganizationTier | null;
  setEditTier: (t: OrganizationTier | null) => void;
  editMaxUsers: string;
  setEditMaxUsers: (v: string) => void;
  onSaveTier: () => void;
  tierUpdateLoading: boolean;
  enrollResult: { enrolledCount: number; skippedCount: number; totalPending: number } | null;
  onEnrollPending: () => void;
  enrollLoading: boolean;
}

function PlanCapacitySection({
  organization,
  isAdmin,
  editTier,
  setEditTier,
  editMaxUsers,
  setEditMaxUsers,
  onSaveTier,
  tierUpdateLoading,
  enrollResult,
  onEnrollPending,
  enrollLoading,
}: PlanCapacitySectionProps) {
  const { t } = useLocale();
  const seatUsage = organization.seatUsage;
  const isEditing = editTier !== null;
  const statusColor = seatUsage ? SEAT_STATUS_COLORS[seatUsage.status] : "text-muted-foreground";
  const barColor = seatUsage ? SEAT_BAR_COLORS[seatUsage.status] : "bg-muted";
  const isUnlimited = seatUsage?.status === "UNLIMITED";

  return (
    <div className="mt-8">
      <h2 className="text-lg font-semibold text-foreground">{t("settings.plan.title")}</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        {isAdmin
          ? t("settings.plan.descriptionAdmin")
          : t("settings.plan.descriptionOrgAdmin")}
      </p>

      <div className="mt-4 rounded-xl border border-border bg-card shadow-sm">
        <div className="p-6 space-y-5">
          {/* Current Tier */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="rounded-md bg-primary/10 p-2">
                <Crown className="h-4 w-4 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t("settings.plan.currentPlan")}</p>
                <div className="flex items-center gap-2">
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-sm font-semibold ${TIER_BADGE_STYLES[organization.tier]}`}
                  >
                    {ORG_TIER_LABELS[organization.tier]}
                  </span>
                  {organization.type === "COMMERCIAL" && (
                    <span className="text-xs text-muted-foreground">
                      ({t(ORG_TIER_DESCRIPTION_KEYS[organization.tier])})
                    </span>
                  )}
                </div>
              </div>
            </div>
            {isAdmin && !isEditing && (
              <button
                type="button"
                onClick={() => {
                  setEditTier(organization.tier);
                  setEditMaxUsers(organization.maxUsers?.toString() ?? "");
                }}
                className="h-8 rounded-md border border-border px-3 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                {t("settings.plan.changePlan")}
              </button>
            )}
            {!isAdmin && organization.tier !== "ENTERPRISE" && (
              <div className="flex items-center gap-2">
                <a
                  href={`mailto:sales@kommit.app?subject=${encodeURIComponent(`Upgrade Request — ${organization.name} (${ORG_TIER_LABELS[organization.tier]} → ${organization.tier === "SEED" ? "Scale" : "Enterprise"})`)}`}
                  className="inline-flex h-8 items-center gap-1.5 rounded-md bg-primary px-3 text-xs font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
                >
                  <ArrowUpCircle className="h-3.5 w-3.5" />
                  {t("settings.plan.requestUpgrade")}
                </a>
                <a
                  href="mailto:sales@kommit.app?subject=Kommit%20Plan%20Inquiry"
                  className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border px-3 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                >
                  <Mail className="h-3.5 w-3.5" />
                  {t("settings.plan.contactSales")}
                </a>
              </div>
            )}
          </div>

          {/* Seat Usage */}
          {seatUsage && (
            <div className="flex items-start gap-3">
              <div className="mt-0.5 rounded-md bg-muted p-2">
                <Gauge className="h-4 w-4 text-muted-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-muted-foreground">{t("settings.plan.seatUsage")}</p>
                <p className={`text-lg font-semibold ${statusColor}`}>
                  {isUnlimited
                    ? t("seats.usageUnlimited", { used: seatUsage.used })
                    : t("seats.usageLimited", { used: seatUsage.used, limit: seatUsage.limit ?? 0 })}
                  {seatUsage.percentage != null && !isUnlimited && (
                    <span className="ml-2 text-sm font-normal text-muted-foreground">
                      ({Math.round(seatUsage.percentage)}%)
                    </span>
                  )}
                </p>
                {!isUnlimited && seatUsage.limit != null && (
                  <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-muted">
                    <div
                      className={`h-full rounded-full transition-all ${barColor}`}
                      style={{ width: `${Math.min(seatUsage.percentage ?? 0, 100)}%` }}
                    />
                  </div>
                )}
                {organization.effectiveMaxUsers != null && organization.maxUsers != null && organization.maxUsers !== organization.effectiveMaxUsers && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t("settings.plan.customLimitOverride", { custom: organization.maxUsers, default: organization.effectiveMaxUsers })}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Tier Editor (ADMIN only) */}
          {isEditing && isAdmin && (
            <div className="rounded-lg border border-primary/20 bg-primary/5 p-4 space-y-4">
              <h4 className="text-sm font-medium text-foreground">{t("settings.plan.updatePlan")}</h4>
              {organization.type === "COMMERCIAL" && (
                <div>
                  <label className="block text-sm font-medium text-foreground">{t("settings.plan.tier")}</label>
                  <div className="mt-1.5 flex gap-2">
                    {ORG_TIERS.map((tierOpt) => (
                      <button
                        key={tierOpt}
                        type="button"
                        onClick={() => setEditTier(tierOpt)}
                        className={`flex-1 rounded-md border px-3 py-2 text-center transition-colors ${
                          editTier === tierOpt
                            ? "border-primary bg-primary/10 text-primary"
                            : "border-border text-muted-foreground hover:bg-muted hover:text-foreground"
                        }`}
                      >
                        <span className="block text-sm font-medium">{ORG_TIER_LABELS[tierOpt]}</span>
                        <span className="block text-xs text-muted-foreground">{t(ORG_TIER_DESCRIPTION_KEYS[tierOpt])}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <label htmlFor="custom-max-users" className="block text-sm font-medium text-foreground">
                  {t("settings.plan.customSeatLimit")}
                </label>
                <input
                  id="custom-max-users"
                  type="number"
                  min={1}
                  value={editMaxUsers}
                  onChange={(e) => setEditMaxUsers(e.target.value)}
                  placeholder={t("adminUI.orgSettings.leaveEmptyForDefault")}
                  className="mt-1.5 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  {t("settings.plan.customSeatLimitHint")}
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={onSaveTier}
                  disabled={tierUpdateLoading}
                  className="inline-flex h-8 items-center gap-1.5 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {tierUpdateLoading ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      {t("settings.saving")}
                    </>
                  ) : (
                    t("settings.plan.savePlan")
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEditTier(null);
                    setEditMaxUsers("");
                  }}
                  className="h-8 rounded-md px-4 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  {t("common.cancel")}
                </button>
              </div>
            </div>
          )}

          {/* Enroll Pending Users CTA */}
          {enrollResult && enrollResult.totalPending > 0 && (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4">
              <p className="text-sm text-amber-600 dark:text-amber-400">
                {t("settings.plan.pendingEnrollment", { count: enrollResult.totalPending })}
              </p>
              <button
                type="button"
                onClick={onEnrollPending}
                disabled={enrollLoading}
                className="mt-3 inline-flex h-8 items-center gap-1.5 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {enrollLoading ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    {t("settings.plan.enrolling")}
                  </>
                ) : (
                  t("settings.plan.enrollPending")
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
